import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Briefcase, MapPin, Calendar, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ApplicationsPage() {
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const token = localStorage.getItem("token");
      const appsResponse = await axios.get(`${API}/applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setApplications(appsResponse.data);

      // Fetch job details for each application
      const jobIds = [...new Set(appsResponse.data.map(app => app.job_id))];
      const jobsData = {};
      
      for (const jobId of jobIds) {
        try {
          const jobResponse = await axios.get(`${API}/jobs/${jobId}`);
          jobsData[jobId] = jobResponse.data;
        } catch (error) {
          console.error(`Failed to fetch job ${jobId}`, error);
        }
      }
      
      setJobs(jobsData);
      setLoading(false);
    } catch (error) {
      toast.error("Failed to load applications");
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-gray-600">Loading your applications...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk', color: '#FF6B6B' }}>
          My Job Applications
        </h1>
        <p className="text-gray-600 mt-2">
          Track all the jobs you've applied to
        </p>
      </div>

      {applications.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Briefcase className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No applications yet</h3>
            <p className="text-gray-600 mb-4">Start applying to jobs to track them here</p>
            <button
              onClick={() => navigate("/jobs")}
              className="px-4 py-2 rounded-lg text-white"
              style={{ background: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 100%)' }}
            >
              Browse Jobs
            </button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {applications.map((application) => {
            const job = jobs[application.job_id];
            if (!job) return null;

            return (
              <Card key={application.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-xl">{job.title}</CardTitle>
                      <CardDescription className="text-base mt-1">
                        {job.company}
                      </CardDescription>
                      <div className="flex flex-wrap gap-2 mt-3">
                        <Badge className="bg-blue-100 text-blue-700">
                          {job.trade_type}
                        </Badge>
                        <Badge variant="outline" className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {job.city}, {job.county}
                        </Badge>
                        <Badge variant="outline">
                          {job.job_type}
                        </Badge>
                        {job.pay_range && (
                          <Badge variant="outline" className="bg-green-50 text-green-700">
                            {job.pay_range}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="h-4 w-4" />
                      <span>Applied {new Date(application.applied_at).toLocaleDateString()}</span>
                    </div>
                    {job.external_url && (
                      <a
                        href={job.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-teal-600 hover:text-teal-700 flex items-center gap-1 text-sm"
                      >
                        View Original Posting
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                  {application.notes && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-700">
                        <strong>Your notes:</strong> {application.notes}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
